import React from 'react';
import { Activity, Heart, BarChart2, MessageSquare, Clock } from 'lucide-react';
import { SectionHeader } from './SectionHeader';

// Engagement Metrics Component
export function EngagementMetrics({ 
  engagementMetrics, 
  filteredPosts, 
  viewsPerPost, 
  subscriberCount,
  dateRangeLabel 
}) {
  const hasPollData = engagementMetrics?.hasPollData;
  const pollRate = engagementMetrics?.pollRate ?? null;
  
  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      <SectionHeader icon={Activity} title="Engagement Metrics" subtitle={`${filteredPosts.length} posts analyzed • ${dateRangeLabel}`} />
      
      <div className="p-5">
        {/* Engagement Rate Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
          <div className="bg-[#E63946]/10 rounded-lg p-3 text-center border border-[#E63946]/20">
            <Heart size={18} className="mx-auto mb-1 text-[#E63946]" />
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">REACTION RATE</p>
            <p className="text-xl font-bold text-[#E63946]">
              {engagementMetrics?.reactionRate || 0}%
            </p>
            <p className="text-[10px] text-[#6B7280]">{engagementMetrics?.totalReactions || 0} total</p>
          </div>
          <div className="bg-[#8B5CF6]/10 rounded-lg p-3 text-center border border-[#8B5CF6]/20">
            <BarChart2 size={18} className="mx-auto mb-1 text-[#8B5CF6]" />
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">POLL RATE</p>
            {pollRate !== null ? (
              <>
                <p className="text-xl font-bold text-[#8B5CF6]">
                  {pollRate}%
                </p>
                <p className="text-[10px] text-[#6B7280]">
                  {hasPollData 
                    ? `${engagementMetrics?.totalVotes || 0} votes / ${engagementMetrics?.totalPollViews || 0} views`
                    : `${engagementMetrics?.postsWithPolls || 0} polls`
                  }
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-1">
                  <Clock size={14} className="text-[#8B5CF6]" />
                  <p className="text-sm font-medium text-[#8B5CF6]">Pending</p>
                </div>
                <p className="text-[10px] text-[#6B7280]">Awaiting poll data</p>
              </>
            )}
          </div>
          <div className="bg-[#3B82F6]/10 rounded-lg p-3 text-center border border-[#3B82F6]/20">
            <MessageSquare size={18} className="mx-auto mb-1 text-[#3B82F6]" />
            <p className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">AVG REACTIONS</p>
            <p className="text-xl font-bold text-[#3B82F6]">
              {engagementMetrics?.avgReactionsPerPost || 0}
            </p>
            <p className="text-[10px] text-[#6B7280]">per post</p>
          </div>
        </div>
        
        {/* Engagement Progress Bars */}
        <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 border border-[hsl(var(--border))]">
          <p className="text-sm text-white font-medium mb-3">Engagement Breakdown</p>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#6B7280]">Posts with Reactions</span>
                <span className="text-xs text-white">{engagementMetrics?.postsWithReactions || 0}/{filteredPosts.length}</span>
              </div>
              <div className="h-2 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#E63946] to-[#F97316] rounded-full transition-all"
                  style={{ width: `${filteredPosts.length > 0 ? ((engagementMetrics?.postsWithReactions || 0) / filteredPosts.length * 100) : 0}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#6B7280]">ERR (Engagement Rate Reach)</span>
                <span className="text-xs text-white">{viewsPerPost > 0 && subscriberCount ? ((viewsPerPost / subscriberCount) * 100).toFixed(1) : 0}%</span>
              </div>
              <div className="h-2 bg-[hsl(var(--border))] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] rounded-full transition-all"
                  style={{ width: `${Math.min((viewsPerPost / subscriberCount * 100) || 0, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EngagementMetrics;
